import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Pencil, ArrowRight, Info, CheckCircle2 } from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { AskEvaButton } from "@/components/AskEvaButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_META = {
  COVERED_BY_VSME: { label: "Coperto da VSME", className: "bg-green-100 text-green-800 border-green-300" },
  COVERED_DIRECT: { label: "Compilato", className: "bg-green-100 text-green-800 border-green-300" },
  PARTIAL_COVERAGE: { label: "Parziale — integrazione consigliata", className: "bg-amber-100 text-amber-800 border-amber-300" },
  MISSING_MAPPED: { label: "Dato VSME mancante", className: "bg-slate-100 text-slate-600 border-slate-300" },
  MISSING_MEF_ONLY: { label: "Da compilare (MEF)", className: "bg-red-100 text-red-800 border-red-300" },
  NOT_APPLICABLE: { label: "N.A.", className: "bg-slate-100 text-slate-500 border-slate-300" },
};

// Provenienza del requisito (interoperabilità) — distinta dallo stato di verifica del valore.
const PROVENANCE_META = {
  VSME_REUSED: { label: "VSME · Dato riusato", className: "bg-green-50 text-green-800 border-green-300" },
  MEF_SPECIFIC: { label: "MEF · Dato specifico banca", className: "bg-amber-50 text-amber-800 border-amber-300" },
  PARTIAL_ALIGNMENT: { label: "VSME/MEF · Allineamento parziale", className: "bg-sky-50 text-sky-800 border-sky-300" },
};
const ALIGNMENT_LABELS = {
  ALLINEATO: "Allineato a VSME",
  PARZIALE: "Allineamento parziale a VSME",
  SPECIFICO_PMI_BANCHE: "Specifico PMI-Banche",
};

const MAPPING_TYPE_LABELS = { EXACT: "Allineato", PARTIAL: "Parziale", RELATED: "Correlato" };

function formatValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (Array.isArray(v)) {
    return v.map((x) => (x && typeof x === "object")
      ? Object.values(x).filter((y) => y !== null && y !== "" && y !== undefined).join(" · ")
      : String(x)).join("; ");
  }
  if (typeof v === "object") {
    return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join(", ");
  }
  return String(v);
}

function CompletionBar({ value, testid }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2.5 rounded-full bg-slate-100 border border-slate-200" data-testid={testid}>
        <div className="h-full rounded-full bg-green-700 transition-[width]" style={{ width: `${value}%` }} />
      </div>
      <span className="text-sm font-bold text-slate-900 w-12 text-right" data-testid={`${testid}-value`}>{value}%</span>
    </div>
  );
}

function StatCard({ title, stats, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-700">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <CompletionBar value={stats.completion} testid={`${testid}-bar`} />
        <p className="text-xs text-slate-600">
          {stats.covered} coperte · {stats.partial} parziali · {stats.missing} mancanti su {stats.total}
        </p>
      </CardContent>
    </Card>
  );
}

function DatapointRow({ item, canWrite, onEdit }) {
  const meta = STATUS_META[item.status] || { label: item.status, className: "" };
  const prov = PROVENANCE_META[item.provenance];
  return (
    <div className="py-3 px-1 flex flex-wrap items-start gap-3 border-b border-slate-100 last:border-0"
      data-testid={`mef-dp-${item.datapoint_code}`}>
      <div className="w-48 shrink-0 space-y-1.5">
        <p className="text-xs font-bold text-slate-900"
          data-testid={`mef-official-number-${item.datapoint_code}`}
          title="Numerazione ufficiale del Documento PMI-Banche (dic. 2025) — stabile per ogni indicatore">
          Indicatore MEF n. {item.official_indicator_number ?? "—"}
        </p>
        <p className="font-mono text-[11px] text-slate-500">{item.datapoint_code}</p>
        <div className="flex flex-wrap gap-1">
          <Badge variant="outline"
            className={`${item.priority === 1
              ? "bg-indigo-50 text-indigo-800 border-indigo-300"
              : "bg-slate-50 text-slate-500 border-slate-200"} text-[10px]`}
            data-testid={`mef-priority-${item.datapoint_code}`}
            title="Colonna 'Priorità per microimprese' del Documento (criterio di proporzionalità) — non è uno stato di compilazione né un'importanza ESG">
            P{item.priority} — Priorità {item.priority}
          </Badge>
          {prov && (
            <Badge variant="outline" className={`${prov.className} text-[10px]`}
              data-testid={`mef-provenance-${item.datapoint_code}`}
              title="Origine/interoperabilità del requisito — distinta dallo stato di verifica del valore">
              {prov.label}
            </Badge>
          )}
        </div>
        {item.official_ref && (
          <details className="text-[11px]" data-testid={`mef-ref-toggle-${item.datapoint_code}`}>
            <summary className="text-sky-800 cursor-pointer select-none font-medium">
              Riferimento ufficiale ▾
            </summary>
            <p className="mt-1 text-slate-600 leading-snug" data-testid={`mef-ref-panel-${item.datapoint_code}`}>
              {item.official_ref}
              {item.official_alignment && (
                <span className="block text-slate-500">
                  {ALIGNMENT_LABELS[item.official_alignment] || item.official_alignment}
                </span>
              )}
            </p>
          </details>
        )}
      </div>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm font-medium text-slate-900 leading-snug">{item.title}</p>
        {item.status === "COVERED_BY_VSME" && (
          <p className="mt-1 text-xs font-medium text-green-800 flex items-center gap-1"
            data-testid={`mef-reused-${item.datapoint_code}`}>
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            Dato già disponibile — riutilizzato dal percorso VSME
          </p>
        )}
        {item.status === "PARTIAL_COVERAGE" && (
          <p className="mt-1 text-xs font-medium text-sky-800"
            data-testid={`mef-partial-note-${item.datapoint_code}`}>
            Dato VSME disponibile · Informazione aggiuntiva MEF ancora necessaria
          </p>
        )}
        {item.mapped_values.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {item.mapped_values.map((mv) => (
              <li key={mv.vsme_code} className="text-xs text-slate-600 flex flex-wrap items-center gap-1.5">
                <span className="font-mono">{mv.vsme_code}</span>
                <span className="text-slate-400">({MAPPING_TYPE_LABELS[mv.mapping_type] || mv.mapping_type})</span>
                {mv.present ? (
                  <span className="font-semibold text-slate-800">
                    {formatValue(mv.value)}{mv.unit ? ` ${mv.unit}` : ""}
                  </span>
                ) : (
                  <span className="text-amber-700">dato mancante</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {item.own_value !== null && item.own_value !== undefined && (
          <p className="mt-1 text-xs text-slate-700" data-testid={`mef-own-value-${item.datapoint_code}`}>
            <span className="font-semibold">{formatValue(item.own_value)}{item.unit ? ` ${item.unit}` : ""}</span>
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <Badge variant="outline" className={`${meta.className} whitespace-nowrap`}
          data-testid={`mef-status-${item.datapoint_code}`}>
          {meta.label}
        </Badge>
        {item.editable && canWrite && (
          <Button variant="outline" size="sm" className="h-8" onClick={() => onEdit(item)}
            data-testid={`mef-edit-${item.datapoint_code}`}>
            <Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
            {item.own_value !== null && item.own_value !== undefined ? "Modifica" : "Inserisci"}
          </Button>
        )}
      </div>
    </div>
  );
}

function EditValueDialog({ item, onClose, onSave, saving, value, setValue }) {
  if (!item) return null;
  const dt = item.data_type;
  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="mef-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            <span className="font-mono text-sm text-slate-500 mr-2">{item.datapoint_code}</span>
            {item.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Modifica del valore dell'informazione {item.datapoint_code} del Dossier PMI-Banche
          </DialogDescription>
        </DialogHeader>
        <p className="text-xs text-slate-600 leading-relaxed">{item.description}</p>
        <div className="space-y-2">
          <Label htmlFor="mef-value-input">Valore{item.unit ? ` (${item.unit})` : ""}</Label>
          {dt === "BOOLEAN" ? (
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger data-testid="mef-value-input" aria-label="Valore">
                <SelectValue placeholder="Seleziona" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true" data-testid="mef-value-yes">Sì</SelectItem>
                <SelectItem value="false" data-testid="mef-value-no">No</SelectItem>
              </SelectContent>
            </Select>
          ) : dt === "INTEGER" || dt === "DECIMAL" ? (
            <Input id="mef-value-input" data-testid="mef-value-input" type="number" step="any"
              value={value} onChange={(e) => setValue(e.target.value)} />
          ) : (
            <Textarea id="mef-value-input" data-testid="mef-value-input" rows={4}
              value={value} onChange={(e) => setValue(e.target.value)}
              placeholder={dt === "LIST" ? "Un elemento per riga" : ""} />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="mef-edit-cancel">Annulla</Button>
          <Button className="bg-green-700 hover:bg-green-800" disabled={saving}
            onClick={onSave} data-testid="mef-edit-save">
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MefDashboard() {
  const { companyId, period, can } = useTenant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/mef`);
      setData(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (item) => {
    const current = item.own_value;
    if (item.data_type === "BOOLEAN") {
      setValue(current === true ? "true" : current === false ? "false" : "");
    } else if (Array.isArray(current)) {
      setValue(current.join("\n"));
    } else {
      setValue(current === null || current === undefined ? "" : String(current));
    }
    setEditing(item);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    const dt = editing.data_type;
    const payload = { datapoint_code: editing.datapoint_code };
    if (dt === "BOOLEAN") {
      payload.boolean_value = value === "" ? null : value === "true";
    } else if (dt === "INTEGER" || dt === "DECIMAL") {
      payload.numeric_value = value === "" ? null : Number(value);
    } else if (dt === "LIST") {
      payload.text_value = value.split("\n").map((s) => s.trim()).filter(Boolean);
    } else {
      payload.text_value = value;
    }
    try {
      await api.put(`/companies/${companyId}/periods/${period.id}/mef/values`, payload);
      toast.success("Valore salvato", { description: editing.datapoint_code });
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  if (!period) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center" data-testid="mef-no-period">
        <Landmark className="w-10 h-10 text-slate-400 mx-auto mb-4" aria-hidden="true" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Nessun periodo attivo</h2>
        <p className="text-sm text-slate-600">Crea un periodo di rendicontazione per preparare il dossier banca.</p>
      </div>
    );
  }

  if (loading && !data) {
    return <p className="text-sm text-slate-600" data-testid="mef-loading">Caricamento Dossier PMI-Banche…</p>;
  }

  if (error) {
    return <p className="text-sm text-red-700" data-testid="mef-error">{error}</p>;
  }

  const { framework, stats, sections } = data;
  const canWrite = can("esg:write") && period.status !== "LOCKED";

  return (
    <div className="space-y-6" data-testid="mef-dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-green-800" aria-hidden="true" />
            Dossier PMI-Banche
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {framework.framework_name} — {framework.version_name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AskEvaButton question="Quali informazioni del dossier banca abbiamo già grazie al VSME?" />
          <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300 font-semibold"
            data-testid="mef-framework-status">
            {framework.status}
          </Badge>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-4" data-testid="mef-disclaimer">
        <Info className="w-5 h-5 text-sky-800 shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-xs text-sky-900 leading-relaxed">
          {framework.notes} I dati già presenti nel VSME vengono riutilizzati automaticamente:
          rispondi solo alle informazioni mancanti, dalla pagina o dal Questionario (sezione "Dialogo con la banca").
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Completamento questionario" stats={stats.overall} testid="mef-overall" />
        <StatCard title="Informazioni prioritarie (P1)" stats={stats.priority_1} testid="mef-p1" />
        <StatCard title="Informazioni utili (P2)" stats={stats.priority_2} testid="mef-p2" />
      </div>

      {sections.map((s) => (
        <Card key={s.code} className="border-slate-200" data-testid={`mef-section-${s.code}`}>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-base font-semibold text-slate-900">
                {s.code} — {s.title}
              </CardTitle>
              <div className="w-48">
                <CompletionBar value={s.stats.completion} testid={`mef-section-bar-${s.code}`} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="divide-y-0">
            {s.datapoints.map((item) => (
              <DatapointRow key={item.datapoint_code} item={item} canWrite={canWrite} onEdit={openEdit} />
            ))}
          </CardContent>
        </Card>
      ))}

      <Card className="border-green-200 bg-green-50" data-testid="mef-questionnaire-cta">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-green-900">
            Le informazioni specifiche del dossier si compilano anche dal Questionario,
            sezione <strong>"Dialogo con la banca"</strong>.
          </p>
          <Link to="/questionario"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 rounded-md px-4 py-2 transition-colors"
            data-testid="mef-goto-questionnaire">
            Apri il Questionario <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </CardContent>
      </Card>

      <EditValueDialog item={editing} onClose={() => setEditing(null)} onSave={save}
        saving={saving} value={value} setValue={setValue} />
    </div>
  );
}
